/**
 * Mobile Layout Component
 *
 * Optimized layout for mobile devices focusing on quick capture workflows.
 * Provides a simplified, app-like interface with bottom navigation.
 */

import { Box, Flex, Text, VStack, HStack, Container, Badge } from '@chakra-ui/react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { MaterialIcon } from '../ui/MaterialIcon';
import { gradientVariantForGlyph } from '../../theme/iconGradient';
import { useMobileDetection } from '../../hooks/ui/useMobileDetection';
import { useSafeArea } from '../../hooks/ui/useSafeArea';
import {
  MobileLayoutContext,
  type ShellVisibility,
  type MobileLayoutContextType,
} from '../../contexts/MobileLayoutContext';
import { injectSafeAreaCSS, updateAndroidNavHeight } from '../../utils/safeArea';
import { optimizeViewportForMobile, preventOverscroll } from '../../utils/viewport';
import { Logo } from '../ui/Logo';
import { ROUTES } from '../../constants/routes';

interface MobileLayoutProps {
  children: ReactNode;
}

// Every nav item is a Material Symbols name. The Workspace item used to opt
// into a BowerIcon SVG instead; at 24px it sat on a different optical grid to
// its neighbours and its intrinsic box pushed the label out of line with them.
//
// There is no per-item size any more. Capture carried an `isPrimary` flag whose
// only effect was a 28px glyph against its neighbours' 24px, and the 2026-09
// design is explicit that the primary nav runs on ONE optical size: see
// `NavRailIcon`, which holds Create to the same 22px as every rail item. Inside
// the fixed-size chip below, the odd one out read as misaligned rather than as
// emphasised. Capture's leading position and its label carry it instead.
interface MobileNavItem {
  icon: string;
  label: string;
  path: string;
  description: string;
}

/**
 * One optical size for every glyph in the bar.
 *
 * 24px rather than the rail's 22: the rail sits in an 84px column on a pointer
 * device, this sits in a quarter of a phone's width under a thumb, and the two
 * chrome heights differ. The shared decision is that there is ONE size, not
 * that both shells picked the same number.
 */
const NAV_ICON_SIZE = '24px';

/**
 * The bottom nav's height, the safe-area inset aside. Exported so chrome that
 * floats above the nav (a list's selection bar) sits on it, not under it.
 */
export const MOBILE_BOTTOM_NAV_HEIGHT_PX = 72;

/** Entity detail routes that should highlight the Workspace tab */
const WORKSPACE_DETAIL_PREFIXES = ['/collections/', '/notes/', '/attachments/'];

const isNavItemActive = (itemPath: string, currentPath: string): boolean => {
  if (currentPath === itemPath) return true;
  if (itemPath !== '/' && currentPath.startsWith(itemPath)) return true;
  if (itemPath === ROUTES.mobileWorkspace) {
    return WORKSPACE_DETAIL_PREFIXES.some((prefix) => currentPath.startsWith(prefix));
  }
  return false;
};

/**
 * What an active item should ANNOUNCE, which is not the same question as
 * whether it is highlighted.
 *
 * `isNavItemActive` is deliberately broader than "this is the page": it lights
 * Workspace for every `WORKSPACE_DETAIL_PREFIXES` route so a note or a
 * collection keeps the tab lit. `aria-current="page"` means the control IS the
 * current page, so reusing it for that prefix match tells a screen-reader user
 * they are on the Workspace page while activating the button navigates them
 * away. `'true'` is the value for an ancestor section.
 *
 * `undefined` rather than `'false'`: the attribute's absence is how "not
 * current" is expressed, and a literal `aria-current="false"` is a token some
 * screen readers announce.
 *
 * The desktop rail DOES meet this, and answers it the other way. Do not read
 * the `aria-pressed` point below as saying otherwise. `NavRailItem:49` emits a
 * flat `aria-current="page"` for any non-toggle active item, and `NavRail`'s
 * Protocols item is a `startsWith` prefix match whose own comment calls it "a
 * section target", so `/protocols/:id` announces "current page" on a button
 * that navigates away. Chat and Workspace dodge it there only because they are
 * panel toggles and take `aria-pressed` instead, which covers two of the
 * rail's items and not the rest. Not fixed here: the rail fix extends this
 * change rather than correcting it (rule 3), and it has its own follow-up.
 */
const ariaCurrentFor = (itemPath: string, currentPath: string): 'page' | 'true' | undefined => {
  if (!isNavItemActive(itemPath, currentPath)) return undefined;
  return currentPath === itemPath ? 'page' : 'true';
};

// Mobile navigation items - focused on core functionality
const MOBILE_NAV_ITEMS: MobileNavItem[] = [
  {
    icon: 'add_circle',
    label: 'Capture',
    path: ROUTES.mobileCapture,
    description: 'Quick capture',
  },
  {
    // Same glyph + label as the desktop nav rail's Chat item — one name for
    // the Bird chat surface across both shells.
    icon: 'chat_bubble',
    label: 'Chat',
    path: ROUTES.mobileBird,
    description: 'AI chat',
  },
  {
    // `folder`, matching the desktop nav rail's Workspace item.
    icon: 'folder',
    label: 'Workspace',
    path: ROUTES.mobileWorkspace,
    description: 'All items',
  },
  {
    icon: 'settings',
    label: 'Settings',
    path: ROUTES.settings,
    description: 'Account settings',
  },
];

const MobileLayout: React.FC<MobileLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPWA, isStandalone } = useMobileDetection();
  const safeArea = useSafeArea();
  // Detect if running inside the Bower native Android app ('BowerApp/1.0' in UA).
  // The native app provides its own AppBar, so the website's mobile header is hidden.
  const isBowerApp = navigator.userAgent.includes('BowerApp');
  // Shell visibility state — child pages can hide header/nav via useMobileLayout()
  const [headerRight, setHeaderRight] = useState<ReactNode>(null);
  const [visibility, setVisibility] = useState<ShellVisibility>({
    header: !isBowerApp, // Native app provides its own AppBar
    bottomNav: true,
  });

  const hideShell = useCallback(() => {
    setVisibility({ header: false, bottomNav: false });
  }, []);

  const showShell = useCallback(() => {
    setVisibility({ header: true, bottomNav: true });
  }, []);

  const setShellVisibility = useCallback((partial: Partial<ShellVisibility>) => {
    setVisibility((prev) => ({ ...prev, ...partial }));
  }, []);

  const isImmersive = !visibility.header && !visibility.bottomNav;
  const isBirdRoute = location.pathname.startsWith(ROUTES.mobileBird);
  // The Bower header is a capture-page affordance only. Everywhere else on
  // mobile it stole 60px from an already short viewport and pushed the page's
  // own heading under the fold, so the route gates it ahead of any page's
  // `setShellVisibility` (a page may still hide it on capture — the recording
  // view does — it just can't turn it on elsewhere).
  const isCaptureRoute = location.pathname.startsWith(ROUTES.mobileCapture);
  // Deliberately NOT `&& !isBowerApp`. The native app's duplicate-AppBar
  // problem is real and pre-existing, but suppressing the header here takes
  // `headerRight` with it — and on capture that slot holds WorkspacePill,
  // the only workspace switcher on the screen where the workspace matters
  // most. Fixing it means giving the capture page a body-rendered switcher
  // first, which is a change of its own.
  const isHeaderVisible = isCaptureRoute && visibility.header;
  const containerMinHeight = !isImmersive && isBirdRoute ? `calc(100vh + 200px)` : undefined;

  const layoutContext: MobileLayoutContextType = useMemo(
    () => ({
      visibility,
      hideShell,
      showShell,
      setShellVisibility,
      isImmersive,
      headerRight,
      setHeaderRight,
    }),
    [visibility, hideShell, showShell, setShellVisibility, isImmersive, headerRight],
  );

  const visibleNavItems = MOBILE_NAV_ITEMS;

  // Theme colors
  const borderColor = 'border.default';
  // `bg.elevated`, NOT `bg.surface`. The header and bottom nav are white chrome
  // floating over the page, and the page below is `bg.canvas` — which the
  // 2026-09 flip made byte-identical to `bg.surface` in light mode, so the
  // chrome was painting the page's own fill and surviving on its hairline
  // alone. Mobile never got the curved frame (Layout returns <MobileLayout>
  // before the frame box), so there is no `frame.panel` here to sit on;
  // `bg.elevated` restores the white these bars had before the flip.
  const navBgColor = 'bg.elevated';
  const primaryColor = 'text.primary';

  // Header height adjustment for PWA and safe areas
  const headerHeight = isStandalone ? '44px' : '60px';
  const bottomNavHeight = `${MOBILE_BOTTOM_NAV_HEIGHT_PX}px`;

  // Initialize safe area CSS and mobile optimizations
  useEffect(() => {
    // Initialize viewport and safe area handling
    optimizeViewportForMobile();
    injectSafeAreaCSS();
    updateAndroidNavHeight();
    preventOverscroll();

    const handleResize = () => updateAndroidNavHeight();
    const handleOrientationChange = () => {
      setTimeout(updateAndroidNavHeight, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Dynamic padding based on which chrome elements are visible.
  //
  // Three cases, and the middle one is the whole point: the header used to be
  // the only thing reserving the top inset, and `viewport-fit=cover` (set in
  // both index.html and optimizeViewportForMobile) puts the layout viewport
  // UNDER the status bar. Nothing else pads the top — `.safe-area-full-height`
  // sets height only, and theme/styles.ts pads left/right — so collapsing to
  // '0' everywhere the header stopped rendering would slide every non-capture
  // page under the notch. Which is the bug this change exists to fix, moved.
  //
  // A page that hid the shell ITSELF (bird, live, onboarding, the recording
  // takeover) still gets '0': it asked for the full viewport and manages its
  // own insets. That case is unchanged from before the route gate.
  const contentPaddingTop = isHeaderVisible
    ? `calc(${headerHeight} + ${safeArea.insets.top}px)`
    : visibility.header
      ? `${safeArea.insets.top}px`
      : '0';
  const contentPaddingBottom = visibility.bottomNav
    ? `calc(${bottomNavHeight} + 32px + ${safeArea.insets.bottom}px)`
    : '0';

  return (
    <MobileLayoutContext.Provider value={layoutContext}>
      <Box
        data-print="scroller"
        height="100vh"
        overflow="hidden"
        bg="bg.canvas"
        position="relative"
        className="safe-area-full-height"
        // Both paddings are published as custom properties so a page can size
        // itself to the space actually left between the header and the bottom
        // nav — `flex: 1` can't, because the Container between here and the
        // page is a plain block.
        sx={{
          '--mobile-content-top': contentPaddingTop,
          '--mobile-content-bottom': contentPaddingBottom,
        }}
      >
        {/* Mobile Header — capture page only, removed from DOM otherwise */}
        {isHeaderVisible && (
          <Box
            data-print="hide"
            // Anchored at 0 and padded by the inset, not offset by it: a bar
            // pushed down to `top={inset}` leaves a canvas-coloured strip
            // under the notch/status bar that the page then scrolls through.
            // Total occupied height stays `headerHeight + inset`, which is
            // what `contentPaddingTop` reserves.
            height={`calc(${headerHeight} + ${safeArea.insets.top}px)`}
            paddingTop={`${safeArea.insets.top}px`}
            borderBottom="1px"
            borderColor={borderColor}
            bg={navBgColor}
            position="fixed"
            top={0}
            left={0}
            right={0}
            zIndex={1000}
            px={4}
          >
            {/* `100%` of the CONTENT box, which is already headerHeight minus
                the border. A literal `headerHeight` here overflows by 1px,
                because box-sizing is border-box and paddingTop now eats the
                inset. */}
            <Flex height="100%" alignItems="center" justifyContent="space-between">
              <HStack
                spacing={2}
                cursor="pointer"
                onClick={() => navigate(ROUTES.root)}
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
              >
                {/* The lockup CONTAINS the wordmark. The `<Text>Bower</Text>`
                    that used to sit here paired with the old icon-only mark;
                    against the lockup it rendered "Bower" twice, side by side. */}
                <Logo size="24px" />
                {isPWA && <Badge variant="success">App</Badge>}
              </HStack>
              {headerRight}
            </Flex>
          </Box>
        )}

        {/* Main Content Area */}
        <Box
          data-print="scroller"
          paddingTop={contentPaddingTop}
          paddingBottom={contentPaddingBottom}
          height="100vh"
          overflowY={isImmersive ? 'hidden' : 'scroll'}
          overflowX="hidden"
          sx={{
            WebkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
            overscrollBehaviorY: 'auto',
            overscrollBehaviorX: 'none',
            touchAction: 'pan-y',
          }}
        >
          <Container
            maxW="container.sm"
            p={0}
            pb={isImmersive ? 0 : 4}
            minHeight={containerMinHeight}
            sx={
              !isImmersive && isBirdRoute
                ? {
                    '&:after': {
                      content: '""',
                      display: 'block',
                      height: '200px',
                      width: '1px',
                    },
                  }
                : undefined
            }
          >
            {children}
          </Container>
        </Box>

        {/* Bottom Navigation — removed from DOM when hidden */}
        {visibility.bottomNav && (
          <Box
            data-print="hide"
            height={bottomNavHeight}
            borderTop="1px"
            borderColor={borderColor}
            bg={navBgColor}
            position="fixed"
            bottom={0}
            left={0}
            right={0}
            zIndex={1000}
            px={2}
            pt={2}
            pb={`max(${safeArea.insets.bottom}px, 16px)`}
            className="safe-bottom-nav"
          >
            <HStack spacing={0} justify="space-around" align="center" height="56px">
              {visibleNavItems.map((item) => {
                const isActive = isNavItemActive(item.path, location.pathname);

                const handleClick = () => {
                  navigate(item.path);
                };

                return (
                  <VStack
                    as="button"
                    type="button"
                    key={item.path}
                    // The chip below is a VISUAL-only state carrier, so without
                    // this the change would have improved what a sighted user
                    // sees while leaving a screen-reader user four identical
                    // buttons. `aria-current`, not `aria-pressed`: every
                    // MOBILE_NAV_ITEMS entry has a `path` and calls `navigate`,
                    // so these are route targets, and `NavRailItem` records why
                    // a toggle must never claim to be a page.
                    //
                    // TWO values, because `isActive` is broader than "this IS
                    // the page". `isNavItemActive` also lights Workspace for any
                    // WORKSPACE_DETAIL_PREFIXES path, so on `/notes/abc` a flat
                    // `'page'` would tell a screen-reader user that the
                    // Workspace button is the page they are on while activating
                    // it navigates somewhere else. `'true'` is the value for an
                    // ancestor section. See `ariaCurrentFor` for why the desktop
                    // rail is NOT evidence that a flat `'page'` is fine.
                    aria-current={ariaCurrentFor(item.path, location.pathname)}
                    spacing={1}
                    flex={1}
                    onClick={handleClick}
                    color={isActive ? primaryColor : 'text.muted'}
                    // NO chip hover, unlike `NavRailItem`. THE ARITHMETIC THAT
                    // ONCE FORCED THIS NO LONGER HOLDS, and the decision stands
                    // on the touch argument below instead.
                    //
                    // What was true until 2026-09-22: the bar's ground is
                    // `bg.elevated` (#242A24 in dark), and `sidebar.item.hover`
                    // WAS that same `DARK_DERIVED.elevated`, so borrowing the
                    // rail's hover measured 1.000:1 against the bar — invisible
                    // — while hovering the ACTIVE tab walked its chip DOWN to
                    // the bar's own fill and erased the state. With only one
                    // fill step above the ground, a hover-then-active ladder
                    // had nowhere to stand.
                    //
                    // The ladder has since moved TWICE, and the arithmetic
                    // now points the other way — which is exactly why it is no
                    // longer what this decision rests on.
                    //
                    // The 2026-09 panel lift took hover to #282E28, measuring
                    // 1.056:1 against this bar: no longer invisible, but under
                    // the 1.10 floor every other hover in the theme is held to
                    // (`surfacePairs.test.tsx`). The 2026-09-22 hairline pass
                    // lifted it again, to `DARK_DERIVED.hover` #2C332C, and
                    // `DARK_DERIVED.active` to #3A403A. Against this bar's
                    // `bg.elevated` #242A24 ground that is now 1.129:1 hover
                    // and 1.379:1 active — so the hover WOULD clear the floor
                    // today.
                    //
                    // It stays declined anyway. The touch argument below is the
                    // only reason, and it is untouched by any of this: do not
                    // restore the chip hover on the strength of the ladder
                    // having moved, in either direction. The numbers above are
                    // recorded so the next reader can see that they were
                    // checked and found irrelevant, not overlooked.
                    //
                    // Dropping it rather than restyling costs nothing real:
                    // `useMobileDetection().isMobile` is the breakpoint AND a
                    // touch check, so this shell only renders where hover is
                    // either never fired or fired as sticky-hover after a tap.
                    //
                    // And be exact about what is left, because "the label keeps
                    // its own hover" is FALSE and is the sentence that would let
                    // a future reader accept this as complete: the bar has no
                    // hover affordance at all. The `color` / `_hover` /
                    // `transition` trio below is inert in both directions. The
                    // label is a Chakra `Text` whose `baseStyle` pins
                    // `color: TEXT_PRIMARY` (`theme/components.ts`), which beats
                    // anything inherited from here, and the glyph is
                    // gradient-painted, which forces `color: transparent`.
                    // Removing the trio is deferred as adjacent cleanup (rule 3),
                    // not left because it works.
                    _hover={{ color: primaryColor }}
                    transition="color 0.2s"
                    py={1}
                    px={1}
                    justify="center"
                    align="center"
                    minH="48px"
                    userSelect="none"
                    _focus={{ outline: 'none', boxShadow: 'none' }}
                    _focusVisible={{ outline: 'none', boxShadow: 'none' }}
                    sx={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {/* The 44x32 chip from the 2026-09 design, minus the rail's
                        hover, for the reason given above. The desktop rail's
                        chip has since grown to match its Create button
                        (`NAV_RAIL_TARGET_SIZE`); this bar has no such button
                        to match, so it keeps the original size. It is not
                        decoration: the glyph
                        below is painted in the brand ramp on EVERY item, active
                        or not, exactly as the desktop rail paints its own, so
                        the chip is the only thing left that can show which tab
                        you are on. Before this the icon showed nothing at all,
                        because `MaterialIcon` defaults its colour to
                        `text.secondary` rather than inheriting, and the item's
                        active/inactive colour reached the label only. */}
                    <Box
                      data-nav-chip
                      w="44px"
                      h="32px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="10px"
                      bg={isActive ? 'sidebar.item.active' : 'transparent'}
                      transition="background 0.15s"
                    >
                      <MaterialIcon
                        name={item.icon}
                        // `boxSize` alone: MaterialIcon resolves a scalar
                        // boxSize through `useToken('sizes')` and uses the
                        // result as the glyph's own font size. A paired
                        // `fontSize` is not belt-and-braces, it is a hazard:
                        // the two resolve against DIFFERENT scales, so if
                        // NAV_ICON_SIZE ever becomes a Chakra scale value (`6`),
                        // `boxSize` reads `sizes` while `fontSize` reads
                        // `fontSizes` and the duplicate silently wins with the
                        // wrong number.
                        boxSize={NAV_ICON_SIZE}
                        // Identity icons. The mobile bottom bar IS the primary
                        // nav, the same role the rail plays on desktop, so the
                        // same treatment applies (`theme/iconGradient.ts`).
                        // What makes the ramp safe here is the label directly
                        // beneath every item: the gradient does not clear 3:1
                        // in light mode, so the glyph must never be the only
                        // thing naming the target.
                        gradient={gradientVariantForGlyph(item.icon)}
                      />
                    </Box>
                    <Text
                      fontSize="xs"
                      fontWeight={isActive ? 'semibold' : 'normal'}
                      textAlign="center"
                      lineHeight="1"
                      noOfLines={1}
                    >
                      {item.label}
                    </Text>
                  </VStack>
                );
              })}
            </HStack>
          </Box>
        )}
      </Box>
    </MobileLayoutContext.Provider>
  );
};

export default MobileLayout;
